import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { readGuest, clearGuest } from "@/lib/guest";
import { toast } from "sonner";
import { BookmarkCheck } from "lucide-react";

export default function Save() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const guest = readGuest();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  // If user landed on /save without a guest session, send them to meet
  if (!guest.narration) {
    return (
      <div className="min-h-screen flex items-center justify-center text-violet-200">
        <div className="text-center">
          <p className="mb-4 text-violet-300/70">There's no story to save yet.</p>
          <Link to="/meet" className="text-violet-200 underline">Meet a companion first</Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("Please accept the Terms of Service to create your account.");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        email,
        password,
        initial_state: {
          user_name: guest.user_name,
          user_gender: guest.user_gender || null,
          character_id: guest.character_id,
          atmosphere_id: guest.custom_atmosphere ? null : guest.atmosphere_id,
          custom_atmosphere: guest.custom_atmosphere || null,
          default_mode: guest.mode || "sleep",
          last_summary: guest.summary,
          relationship_stage: guest.relationship_stage || "newly acquainted",
          session_count: guest.session_count || 1,
          last_session_narration: guest.narration,
        },
      };
      const { data } = await api.post("/auth/register", payload);
      localStorage.setItem("asmr_token", data.token);
      await refresh();
      clearGuest();
      toast.success("They'll remember you now.");
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your story.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-6 pt-safe-top pb-safe-bottom text-violet-50">

      <div className="relative z-10 w-full max-w-md fade-rise">
        <Link to="/" className="flex items-center gap-2.5 mb-12 justify-center" data-testid="save-back-to-home">
          <img src="/continua-logo-small.png" alt="" aria-hidden="true" className="w-9 h-9 object-contain moon-pulse" />
          <span className="font-serif-display text-xl">Continua</span>
        </Link>

        <div className="glass-strong rounded-2xl p-8 md:p-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-violet-300/90 mb-4">
            <BookmarkCheck className="w-3 h-3" /> Keep this thread
          </div>
          <h1 className="font-serif-display text-3xl md:text-4xl mb-2 leading-tight">Save your story, {guest.user_name}.</h1>
          <p className="text-violet-200/75 mb-8 text-sm leading-relaxed">
            So {guest.character_id === "seriny" ? "Seriny" : "Elizabeth"} remembers you, and the story continues tomorrow.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-violet-300/70 mb-2 block">Email</label>
              <input
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="save-email-input"
                className="w-full bg-indigo-950/40 border border-violet-300/20 rounded-xl px-4 py-3 text-violet-50 focus:outline-none focus:border-violet-300/50 focus:ring-1 focus:ring-violet-300/40 transition-soft"
                placeholder="you@there.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.25em] text-violet-300/70 mb-2 block">Password</label>
              <input
                type="password" required minLength={6} value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="save-password-input"
                className="w-full bg-indigo-950/40 border border-violet-300/20 rounded-xl px-4 py-3 text-violet-50 focus:outline-none focus:border-violet-300/50 focus:ring-1 focus:ring-violet-300/40 transition-soft"
                placeholder="at least 6 characters"
              />
            </div>
            <label
              className="flex items-start gap-3 cursor-pointer select-none group"
              data-testid="tos-agree-label"
            >
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                data-testid="tos-agree-checkbox"
                className="peer sr-only"
              />
              <span
                aria-hidden="true"
                className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-md border flex items-center justify-center transition-soft ${
                  agreed
                    ? "bg-violet-200 border-violet-200"
                    : "bg-indigo-950/40 border-violet-300/30 group-hover:border-violet-300/60"
                }`}
              >
                {agreed && (
                  <svg viewBox="0 0 12 10" className="w-3 h-3 text-indigo-950" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 5.5 4.2 8.5 11 1.5" />
                  </svg>
                )}
              </span>
              <span className="text-xs text-violet-300/70 leading-relaxed">
                I agree to the{" "}
                <a
                  href="/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tos-terms-link"
                  onClick={(e) => e.stopPropagation()}
                  className="text-violet-200 underline underline-offset-2 hover:text-white transition-soft"
                >
                  Terms of Service
                </a>{" "}
                and acknowledge that I have read the{" "}
                <a
                  href="/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tos-privacy-link"
                  onClick={(e) => e.stopPropagation()}
                  className="text-violet-200 underline underline-offset-2 hover:text-white transition-soft"
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>
            <div
              onClick={() => {
                if (!agreed && !loading) toast.error("Please accept the Terms of Service to create your account.");
              }}
              data-testid="save-submit-wrapper"
            >
              <button
                type="submit" disabled={loading || !agreed}
                data-testid="save-submit-button"
                className="w-full rounded-full px-6 py-3.5 bg-violet-200 text-indigo-950 font-medium hover:bg-violet-100 transition-soft disabled:opacity-50 disabled:pointer-events-none glow-violet"
              >
                {loading ? "Saving…" : "Save and continue"}
              </button>
            </div>
          </form>

          <p className="text-sm text-violet-300/60 text-center mt-8">
            Already have an account?{" "}
            <Link to="/login" data-testid="save-login-link" className="text-violet-200 hover:text-white">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
